import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

type Event = {
  id: number;
  source: string;
  event_type: string;
  occurred_at: string;
  ingested_at: string;
  payload: Record<string, unknown> | null;
};

const SOURCE_BADGE: Record<string, string> = {
  gmail_ca:         'bg-rose-50 text-rose-700 border-rose-200',
  icloud_com:       'bg-sky-50 text-sky-700 border-sky-200',
  amazon_com:       'bg-orange-50 text-orange-700 border-orange-200',
  easycentral:      'bg-emerald-50 text-emerald-700 border-emerald-200',
  alphazone:        'bg-indigo-50 text-indigo-700 border-indigo-200',
  comfyship:        'bg-violet-50 text-violet-700 border-violet-200',
  carrier_api:      'bg-amber-50 text-amber-700 border-amber-200',
  manual:           'bg-slate-100 text-slate-700 border-slate-200',
  migration_legacy: 'bg-slate-50 text-slate-500 border-slate-200',
};

/** Bir sipariş için order_events tablosundan son N event'i listeler. */
export default function EventLog({ sellerOrderId, limit = 30 }: { sellerOrderId: string; limit?: number }) {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const { data } = await supabase
        .from('order_events')
        .select('id, source, event_type, occurred_at, ingested_at, payload')
        .eq('seller_order_id', sellerOrderId)
        .order('occurred_at', { ascending: false })
        .limit(limit);
      if (!cancelled) {
        setEvents((data as Event[]) ?? []);
        setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [sellerOrderId, limit]);

  if (loading) return <div className="text-xs text-slate-400">Event log yükleniyor…</div>;
  if (events.length === 0) return <div className="text-xs text-slate-400">Bu sipariş için event yok.</div>;

  return (
    <ol className="space-y-1.5">
      {events.map((e) => (
        <li key={e.id} className="border-l-2 border-slate-200 pl-3 py-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={'inline-block px-1.5 py-0.5 rounded text-[10px] font-medium border ' + (SOURCE_BADGE[e.source] ?? 'bg-slate-100 text-slate-600 border-slate-200')}>
              {e.source}
            </span>
            <span className="text-xs font-medium text-slate-700">{e.event_type}</span>
            <span className="text-[10px] text-slate-400">
              {new Date(e.occurred_at).toLocaleString('tr-TR', {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
          {e.payload && Object.keys(e.payload).length > 0 && (
            <details className="mt-1">
              <summary className="text-[10px] text-slate-400 cursor-pointer hover:text-slate-600">payload</summary>
              <pre className="text-[10px] bg-slate-50 p-1.5 rounded mt-1 overflow-x-auto">
                {JSON.stringify(e.payload, null, 2)}
              </pre>
            </details>
          )}
        </li>
      ))}
    </ol>
  );
}
